import { marked } from 'marked';

import { ACTION_PLAN_SOURCE_INSIGHT } from 'constants/actionPlan';
import {
  AICompetitiveIntelligenceRelatedDataType,
  RecommendationDetailsType,
  RecommendationType,
} from 'types/recommendations';
import { isObject } from 'utils/helpers/object';
import { normalizeKey } from 'utils/helpers/string';

import {
  AI_CI_CONFIG_NAME_PREFIX,
  AI_CI_RANKING_TABS,
  AICIRankingTabKey,
  CATEGORY_VALUE_WIDGETS,
  OVERALL_VIEW_KEY,
  RECOMMENDATION_CATEGORIES,
  SALES_TALK_TRACK_CATEGORY_ID_PREFIX,
  SALES_TALK_TRACKS_ROUTE,
  UNDEFINED_CATEGORY,
} from './constants';

type RankingTab = (typeof AI_CI_RANKING_TABS)[number];
const HASH_ID_START_INDEX = 1;

const ACTION_PLAN_SOURCE_INSIGHT_BY_TAB: Partial<Record<AICIRankingTabKey, ACTION_PLAN_SOURCE_INSIGHT>> = {
  online_reviews: ACTION_PLAN_SOURCE_INSIGHT.ONLINE_REVIEWS,
  apartments: ACTION_PLAN_SOURCE_INSIGHT.APARTMENTS,
  dining: ACTION_PLAN_SOURCE_INSIGHT.DINING,
  activities: ACTION_PLAN_SOURCE_INSIGHT.ACTIVITIES,
  amenities: ACTION_PLAN_SOURCE_INSIGHT.AMENITIES,
  community_environment: ACTION_PLAN_SOURCE_INSIGHT.COMMUNITY_FIRST_IMPRESSION,
  website_experience: ACTION_PLAN_SOURCE_INSIGHT.WEBSITE_EXPERIENCE,
};

export const unwrap = (field: any): any => (isObject(field) && 'value' in field ? field.value : field);

export const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
};

export const getCategoryOrDefaultScheme = (category: string) => {
  const normalizedCategoryKey = normalizeKey(category) as keyof typeof RECOMMENDATION_CATEGORIES;
  return RECOMMENDATION_CATEGORIES[normalizedCategoryKey] || UNDEFINED_CATEGORY;
};

export const getRecommendationDefaultCategoryScheme = (recommendation: RecommendationType) => {
  const dataPoints = recommendation.config.data_points.sort((dp1, dp2) => dp1.order - dp2.order);
  if (dataPoints.length === 0) {
    return UNDEFINED_CATEGORY;
  }

  const categoryName = dataPoints[0].category;
  return getCategoryOrDefaultScheme(categoryName);
};

export const getAvailableWidgets = (recommendation: RecommendationDetailsType) => {
  const dataPointValues = recommendation.config.data_points.map((dp) => dp.value);
  const allWidgets = dataPointValues.reduce((acc, val) => {
    if (val in CATEGORY_VALUE_WIDGETS) {
      const widgets = CATEGORY_VALUE_WIDGETS[val as keyof typeof CATEGORY_VALUE_WIDGETS] || [];
      return acc.concat(widgets);
    }

    return acc;
  }, [] as number[]);

  return Array.from(new Set(allWidgets));
};

export const findAICIRelatedDataByTabLabel = (
  relatedData: AICompetitiveIntelligenceRelatedDataType[],
  label?: string
) => {
  if (!label) {
    return undefined;
  }

  return relatedData.find(
    (item) => item.display_label === label || item.config_name === `${AI_CI_CONFIG_NAME_PREFIX}${label}`
  );
};

export const getAICIRankingTabRank = (
  tab: RankingTab,
  relatedData: AICompetitiveIntelligenceRelatedDataType[],
  overallRanking?: number | null
) => {
  if (tab.key === OVERALL_VIEW_KEY) {
    return overallRanking ?? null;
  }

  return findAICIRelatedDataByTabLabel(relatedData, tab.label)?.ranking ?? null;
};

export const parseTextFromMarkDown = (value: string) => {
  const htmlString = marked(value, { async: false });
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const walker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT);

  const textList = [];
  let currentNode: Node | null = walker.currentNode;

  while (currentNode) {
    textList.push(currentNode.textContent);
    currentNode = walker.nextNode();
  }

  return textList;
};

export const normalizeMarkdown = (value: string) => {
  return value.replace(/^\s*•\s+/gm, '- '); // bullets to dash
};

export const getSalesTalkTrackCategoryId = (categoryLabel: string) =>
  `${SALES_TALK_TRACK_CATEGORY_ID_PREFIX}${normalizeKey(categoryLabel)}`;

export const getSalesTalkTrackCategoryPath = (categoryLabel?: string) => {
  if (!categoryLabel) {
    return SALES_TALK_TRACKS_ROUTE;
  }

  return `${SALES_TALK_TRACKS_ROUTE}#${getSalesTalkTrackCategoryId(categoryLabel)}`;
};

export const getRelatedSalesTalkTrackCategoryId = (categoryId: string, categories: Array<{ title: string }>) => {
  const targetKey = categoryId.startsWith(SALES_TALK_TRACK_CATEGORY_ID_PREFIX)
    ? categoryId.slice(SALES_TALK_TRACK_CATEGORY_ID_PREFIX.length)
    : categoryId;

  const relatedCategory = categories.find((category) => {
    const categoryKey = normalizeKey(category.title);
    return categoryKey === targetKey || categoryKey.includes(targetKey) || targetKey.includes(categoryKey);
  });

  return relatedCategory ? getSalesTalkTrackCategoryId(relatedCategory.title) : null;
};

export const getActionPlanSourceInsightIdByTab = (tabKey: AICIRankingTabKey) =>
  ACTION_PLAN_SOURCE_INSIGHT_BY_TAB[tabKey] ?? null;

export const scrollToSection = (sectionId: string) => {
  const el = document.getElementById(sectionId);
  if (!el) {
    return false;
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
};

export const getHashTargetId = (hash: string) => {
  if (!hash) {
    return '';
  }

  return hash.slice(HASH_ID_START_INDEX);
};

export const scrollToSalesTalkTrackHashTarget = (hash: string, categories: Array<{ title: string }>) => {
  const targetId = getHashTargetId(hash);
  if (!targetId) {
    return;
  }

  if (scrollToSection(targetId)) {
    return;
  }

  const relatedCategoryId = getRelatedSalesTalkTrackCategoryId(targetId, categories);
  if (relatedCategoryId) {
    scrollToSection(relatedCategoryId);
  }
};
